import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import './Register.css';

const firstMessage = (data) => {
  if (!data) return 'Registration failed';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  const v = Object.values(data)[0];
  return Array.isArray(v) ? v[0] : String(v);
};

const Register = () => {
  const [searchParams] = useSearchParams();
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [invite, setInvite] = useState(null); // {valid, name, phone_hint}
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [consentContact, setConsentContact] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const normalizeDigits = (s) => (s || '').replace(/\D/g, '');

  const checkInvite = async () => {
    if (!inviteCode.trim()) return;
    try {
      const res = await api.post('/api/invites/validate/', { code: inviteCode });
      setInvite(res.data);
      if (!res.data.valid) toast.error('This invite code is invalid, used or expired.');
    } catch (err) {
      toast.error('Could not check the invite code. Please try again.');
    }
  };

  useEffect(() => {
    if (inviteCode) checkInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendOtp = async () => {
    try {
      await api.post('/api/auth/otp/send/', { phone: mobile, invite_code: inviteCode });
      setOtpSent(true);
      toast.success('Verification code sent.');
    } catch (err) {
      toast.error(firstMessage(err.response?.data));
    }
  };

  const verifyOtp = async () => {
    try {
      await api.post('/api/auth/otp/verify/', { phone: mobile, code: otp });
      setPhoneVerified(true);
      toast.success('Phone verified.');
    } catch (err) {
      toast.error(firstMessage(err.response?.data));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!invite?.valid) {
      toast.error('Enter a valid invite code.');
      return;
    }
    if (!fullName || !username || !email || !password || !mobile) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!phoneVerified) {
      toast.error('Please verify your mobile number.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (!consentContact) {
      toast.error('Please authorize FO Platform to contact you for sign-up and service information.');
      return;
    }
    // single agreement checkbox covers both Terms and Privacy
    if (!agreeTerms) {
      toast.error('You must agree to the Terms & Conditions and Privacy Policy to register.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/users/', {
        username,
        email,
        password,
        full_name: fullName,
        phone: normalizeDigits(mobile),
        invite_code: inviteCode,
      });
      setLoading(false);
      toast.success('Registration successful. Please login.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setLoading(false);
      toast.error(firstMessage(err.response?.data));
    }
  };

  return (
    <div className="container registerGrid">
      <aside className="promo" aria-hidden="true">
        <div className="promoInner">
          <div className="brand">
            <div className="logoBox">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="brandTitle">FraxLandShare</h3>
          </div>
          <h3 className="promoTitle">Registration is by invitation</h3>
          <p className="promoText">Secure SPVs, verified documentation, and transparent exits.</p>
          <img src="/hero-land-investment.jpg" alt="" className="promoImage" />
        </div>
      </aside>

      <form onSubmit={handleSubmit} className="form">
        <div className="formHeader">
          <div className="logoBoxSmall">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="title">Create an account</h2>
            <p className="subtitle">Create your investor account</p>
          </div>
        </div>

        <div className="formGrid">
          <div className="full">
            <label className="label" htmlFor="inviteCode">Invite code</label>
            <input id="inviteCode" value={inviteCode} onChange={e=>{setInviteCode(e.target.value); setInvite(null);}} onBlur={checkInvite} placeholder="Enter your invite code" className="input" autoComplete="off" />
            {invite?.valid && <p className="subtitle">Invite found for {invite.name} (mobile ending {invite.phone_hint.slice(-4)}).</p>}
            {invite && !invite.valid && <p role="alert" className="subtitle">This invite code is invalid, used or expired. <Link to="/contact">Request an invite</Link></p>}
          </div>

          <div>
            <label className="label">Full Name</label>
            <input value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="Enter your full name" className="input" />
          </div>

          <div>
            <label className="label">Username</label>
            <input value={username} onChange={e=>setUsername(e.target.value)} placeholder="Choose a username" className="input" />
          </div>

          <div>
            <label className="label">Email</label>
            <input value={email} onChange={e=>setEmail(e.target.value)} type="email" placeholder="you@example.com" className="input" />
          </div>

          <div>
            <label className="label" htmlFor="mobile">Mobile number</label>
            <input id="mobile" value={mobile} onChange={e=>{setMobile(e.target.value); setOtpSent(false); setPhoneVerified(false);}} type="tel" placeholder="The number your invite was issued to" className="input" disabled={phoneVerified} />
            {!phoneVerified && <button type="button" onClick={sendOtp} disabled={!invite?.valid || !mobile} className="submitButton">{otpSent ? 'Resend code' : 'Send verification code'}</button>}
            {otpSent && !phoneVerified && (
              <>
                <label className="label" htmlFor="otp">Verification code</label>
                <input id="otp" value={otp} onChange={e=>setOtp(e.target.value)} inputMode="numeric" maxLength={6} className="input" autoComplete="one-time-code" />
                <button type="button" onClick={verifyOtp} disabled={otp.length !== 6} className="submitButton">Verify</button>
              </>
            )}
            {phoneVerified && <p className="subtitle">Mobile number verified ✓</p>}
          </div>

          <div>
            <label className="label">Password</label>
            <input value={password} onChange={e=>setPassword(e.target.value)} type="password" placeholder="Create password" className="input" />
          </div>

          <div>
            <label className="label">Confirm password</label>
            <input value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} type="password" placeholder="Repeat password" className="input" />
          </div>
        </div>

        {/* Consents */}
        <div className="formGrid">
          <div className="full consentSection">
            <label className="consentLabel">
              <input type="checkbox" checked={consentContact} onChange={e=>setConsentContact(e.target.checked)} className="checkbox" />
              <span>I authorize Frax Land Share to contact me (email, call, message or WhatsApp) for sign-up and service information, even if I'm on DND.</span>
            </label>
          </div>

          <div className="full">
            <label className="consentLabel">
              <input type="checkbox" checked={agreeTerms} onChange={e=>setAgreeTerms(e.target.checked)} className="checkbox" />
              <span>
                I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="termsLink">Terms and Conditions</a> and have read and accept the <a href="/privacy" target="_blank" rel="noreferrer" className="termsLink">Privacy Policy</a>
              </span>
            </label>
          </div>
        </div>

        <div className="formGrid">
          <div className="full">
            <button type="submit" disabled={loading} className="submitButton">
              {loading ? 'Creating...' : 'Register'}
            </button>
          </div>

          <div className="full loginLink">
            Already have an account? <Link to="/login">Login</Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Register;
